import flowChatDefaultLegacy from '../flows/flow-chat-default-legacy.json';
import flowChatDenuncia from '../flows/flow-chat-denuncia.json';
import flowChatEsg from '../flows/flow-chat-esg.json';
import flowChatWwcalc from '../flows/flow-chat-wwcalc.json';
import flowChatPublicidade from '../flows/flow-chat-publicidade.json';

const FALLBACK_FLOWS = {
    'pesquisa-de-compliance': flowChatDefaultLegacy.flow || null,
    'pesquisa-de-clima': flowChatDefaultLegacy.flow || null,
    'recrutamento-e-selecao': flowChatDefaultLegacy.flow || null,
    denuncia: flowChatDenuncia.flow || null,
    esg: flowChatEsg.flow || null,
    wwcalc: flowChatWwcalc.flow || null,
    publicidade: flowChatPublicidade.flow || null,
};

const getLocalFlowDefinition = (tipo) => FALLBACK_FLOWS[tipo] || null;

const normalizeNumericAnswer = (value) => {
    if (value === undefined || value === null) return null;
    const normalized = Number(value);
    return Number.isNaN(normalized) ? null : normalized;
};

const normalizeYesNo = (value = '') => {
    if (value === undefined || value === null) return '';
    const text = value.toString().trim().toLowerCase();
    if (!text) return '';
    if (['sim', 's', 'yes', 'y'].includes(text)) return 'yes';
    if (['nao', 'não', 'n', 'no'].includes(text)) return 'no';
    return '';
};

const isYesNoQuestion = (question) => question?.answerType === 'yesno';

const getInvalidAnswerConfig = (question) => {
    if (!question) return null;
    if (question.invalidAnswer) return question.invalidAnswer;
    if (isYesNoQuestion(question)) {
        return { message: 'Por favor, responda com sim ou não.', video: 'simnao' };
    }
    if (question.type === 'scale' || question.scale) {
        const min = question.scale?.min ?? 1;
        const max = question.scale?.max ?? 5;
        return {
            message: `Por favor, digite um número entre ${min} e ${max} para responder.`,
            video: '1a5'
        };
    }
    return null;
};

const normalizeAnswerForQuestion = (question, input) => {
    if (!question) return { valid: false, normalized: '' };
    if (question.requiresResponse === false) {
        return { valid: false, normalized: '' };
    }
    if (isYesNoQuestion(question)) {
        const normalized = normalizeYesNo(input);
        return normalized
            ? { valid: true, normalized: normalized === 'yes' ? 'sim' : 'nao' }
            : { valid: false, normalized: '', invalid: getInvalidAnswerConfig(question) };
    }
    if (question.type === 'scale' || question.scale) {
        if (input === undefined || input === null) {
            return { valid: false, normalized: '', invalid: getInvalidAnswerConfig(question) };
        }
        const normalizedInput = input.toString().replace(',', '.');
        const parsed = Number.parseFloat(normalizedInput);
        if (Number.isNaN(parsed)) {
            return { valid: false, normalized: '', invalid: getInvalidAnswerConfig(question) };
        }
        const rounded = Math.round(parsed);
        const min = question.scale?.min ?? 1;
        const max = question.scale?.max ?? 5;
        if (rounded < min || rounded > max) {
            return { valid: false, normalized: '', invalid: getInvalidAnswerConfig(question) };
        }
        return { valid: true, normalized: String(rounded) };
    }
    return { valid: true, normalized: input };
};

const shouldEndAfterVideo = (question) =>
    question?.endAfterVideo === true && question?.requiresResponse === false;

const getFinalQuestion = (questions = []) => {
    if (!Array.isArray(questions) || !questions.length) return null;
    return (
        questions.find((question) => shouldEndAfterVideo(question)) ||
        questions[questions.length - 1]
    );
};

const getFinalClipKey = (questions = []) => {
  const question = getFinalQuestion(questions);
  return question?.video || question?.id || '';
};

const getIntroClipKey = (script = {}) =>
  script?.avatars?.[0]?.video || 'intro';

const shouldEndAfterVideoForClip = (questions = [], clipKey, fallbackIndex) => {
    if (!Array.isArray(questions) || !questions.length) return false;
    const resolvedIndex = typeof clipKey === 'string' && clipKey
        ? questions.findIndex((question, index) => (question?.video || question?.id || `q${index + 1}`) === clipKey)
        : -1;
    const index = resolvedIndex >= 0 ? resolvedIndex : (Number.isFinite(fallbackIndex) ? fallbackIndex : -1);
    if (index < 0 || index >= questions.length) return false;
    return shouldEndAfterVideo(questions[index]);
};

const toNumberArray = (value) => {
    if (Array.isArray(value)) return value.filter((item) => typeof item === 'number' && Number.isFinite(item));
    if (typeof value === 'number' && Number.isFinite(value)) return [value];
    return [];
};

const getQuestionIdFromResponse = (response, questions = []) => {
    if (!response) return null;
    if (typeof response.questionId === 'string' && response.questionId.trim()) {
        return response.questionId.trim();
    }
    const index = Number.isFinite(response.qIndex) ? response.qIndex : null;
    const question = index !== null ? questions[index] : null;
    const idCandidate = question?.id || question?.video;
    if (idCandidate) return idCandidate;
    if (typeof response.videoKey === 'string' && response.videoKey.trim()) return response.videoKey.trim();
    if (response.video) return response.video;
    if (typeof index === 'number') return `q${index + 1}`;
    return null;
};

const getQuestionId = (question, index) =>
    question?.id || question?.video || `q${index + 1}`;

const isProgressQuestion = (question) => question?.requiresResponse !== false;

const buildProgressQuestionIdSet = (questions = []) => {
    const ids = new Set();
    questions.forEach((question, index) => {
        if (!isProgressQuestion(question)) return;
        const id = getQuestionId(question, index);
        if (id) ids.add(id);
    });
    return ids;
};

const buildQuestionOrder = (flow = null, questions = []) => {
    const ordered = [];
    const seen = new Set();
    const progressIds = buildProgressQuestionIdSet(questions);
    if (flow?.blocks?.length) {
        flow.blocks.forEach((block) => {
            (block.questions || []).forEach((questionId) => {
                if (!progressIds.has(questionId)) return;
                if (!questionId || seen.has(questionId)) return;
                ordered.push(questionId);
                seen.add(questionId);
            });
        });
    }
    questions.forEach((question, index) => {
        if (!isProgressQuestion(question)) return;
        const questionId = getQuestionId(question, index);
        if (!questionId || seen.has(questionId)) return;
        ordered.push(questionId);
        seen.add(questionId);
    });
    return ordered;
};

const matchesRuleCondition = (when = {}, context = {}) => {
    const questionMatch = !when.questionId || when.questionId === context.questionId;
    const blockMatch = !when.blockId || when.blockId === context.blockId;
    const subflowMatch = !when.subflow || when.subflow === context.subflow;
    const actionMatch = !when.action || when.action === context.action;

    if (!questionMatch || !blockMatch || !subflowMatch || !actionMatch) return false;

    const answerCondition = {
        eq: when['answer.eq'] || when.answer?.eq,
        in: when['answer.in'] || when.answer?.in
    };
    if (answerCondition.eq || answerCondition.in) {
        const answerValue = context.answer;
        if (answerValue === null) return false;
        if (typeof answerValue === 'string') {
            const toStringArray = (value) =>
                Array.isArray(value) ? value.filter((item) => typeof item === 'string') : [];
            if (answerCondition.eq && !toStringArray(answerCondition.eq).includes(answerValue)) {
                return false;
            }
            if (answerCondition.in && !toStringArray(answerCondition.in).includes(answerValue)) {
                return false;
            }
        } else {
            if (answerCondition.eq && !toNumberArray(answerCondition.eq).includes(answerValue)) {
                return false;
            }
            if (answerCondition.in && !toNumberArray(answerCondition.in).includes(answerValue)) {
                return false;
            }
        }
    }

    return true;
};

const normalizeAnswerForRule = (question, response) => {
    if (!response) return null;
    if (question?.answerType === 'yesno') {
        const normalized = normalizeYesNo(response.userResponse);
        return normalized ? (normalized === 'yes' ? 'sim' : 'nao') : null;
    }
    if (question?.type === 'scale' || question?.scale) {
        return normalizeNumericAnswer(response.userResponse);
    }
    // Fallback: para scripts legados sem metadata de escala,
    // ainda tentamos normalizar respostas numéricas (ex.: "1".."5")
    // para permitir matching de regras answer.in/answer.eq numéricas.
    const numericFallback = normalizeNumericAnswer(response.userResponse);
    if (numericFallback !== null) return numericFallback;
    return response.userResponse ?? null;
};

const buildFlowMaps = (flow = {}) => {
    const blockMap = new Map();
    const questionToBlock = new Map();
    const blockOrder = [];
    (flow.blocks || []).forEach((block) => {
        const questions = Array.isArray(block.questions) ? block.questions : [];
        blockMap.set(block.id, questions);
        blockOrder.push(block.id);
        questions.forEach((questionId) => {
            questionToBlock.set(questionId, block.id);
        });
    });
    const questionOrder = blockOrder.flatMap((blockId) => blockMap.get(blockId) || []);
    const subflowMap = new Map((flow.subflows || []).map((subflow) => [subflow.id, subflow]));
    const startBlockId = questionToBlock.get(flow.start) || blockOrder[0] || null;
    return {
        blockMap,
        questionToBlock,
        subflowMap,
        blockOrder,
        questionOrder,
        startBlockId
    };
};

const getDefaultNextQuestion = (questionId, context, maps) => {
    const currentBlockQuestions = maps.blockMap.get(context.currentBlockId) || [];
    if (questionId) {
        const index = currentBlockQuestions.indexOf(questionId);
        if (index >= 0 && index < currentBlockQuestions.length - 1) {
            return currentBlockQuestions[index + 1];
        }
    }
    const blockIndex = maps.blockOrder.indexOf(context.currentBlockId);
    for (let i = blockIndex + 1; i < maps.blockOrder.length; i += 1) {
        const nextBlockId = maps.blockOrder[i];
        const nextBlockQuestions = maps.blockMap.get(nextBlockId) || [];
        if (nextBlockQuestions.length) {
            context.currentBlockId = nextBlockId;
            return nextBlockQuestions[0];
        }
    }
    return null;
};

const applyRuleActions = (actions = [], context, maps) => {
    for (const action of actions) {
        if (action.enterSubflow) {
            const subflow = maps.subflowMap.get(action.enterSubflow);
            context.currentSubflow = action.enterSubflow;
            context.currentBlockId = subflow?.set?.currentBlock || context.currentBlockId;
            context.nextQuestion = subflow?.start || context.nextQuestion;
        }
        if (action.gotoBlock) {
            context.currentSubflow = null;
            context.currentBlockId = action.gotoBlock;
            const blockQuestions = maps.blockMap.get(action.gotoBlock) || [];
            context.nextQuestion = blockQuestions.length ? blockQuestions[0] : context.nextQuestion;
        }
        if (action.gotoQuestion) {
            context.nextQuestion = action.gotoQuestion;
        }
        if (action.exitSubflow) {
            context.currentSubflow = null;
        }
        if (action.set?.currentBlock) {
            context.currentBlockId = action.set.currentBlock;
        }
        if (action.end) {
            context.nextQuestion = null;
        }
    }
};

const determineNextQuestionId = (responses = [], flow = null, questions = []) => {
    if (!flow || !flow.blocks) return null;
    const maps = buildFlowMaps(flow);
    const context = {
        currentBlockId: maps.startBlockId,
        currentSubflow: null,
        nextQuestion: flow.start || maps.questionOrder[0] || null,
    };
    let lastQuestionId = null;
    const rules = flow.rules || [];

    if (!Array.isArray(responses) || !responses.length) {
        return context.nextQuestion;
    }

    responses.forEach((response) => {
        const questionId = getQuestionIdFromResponse(response, questions);
        if (!questionId) return;
        context.currentBlockId = maps.questionToBlock.get(questionId) || context.currentBlockId;
        context.nextQuestion = undefined;
        const blockQuestions = maps.blockMap.get(context.currentBlockId) || [];
        const isSubflowLast = context.currentSubflow && context.currentSubflow === context.currentBlockId && blockQuestions[blockQuestions.length - 1] === questionId;
        const actionName = isSubflowLast ? 'completed' : 'answered';
        const question = questions.find((item) => (item?.id || item?.video) === questionId);
        const answerValue = normalizeAnswerForRule(question, response);
        const matchingRules = rules.filter((rule) => matchesRuleCondition(rule.when, {
            questionId,
            blockId: context.currentBlockId,
            subflow: context.currentSubflow,
            action: actionName,
            answer: answerValue
        }));

        // Prioriza regras mais especificas por resposta (answer.eq / answer.in)
        // para evitar que regras genericas (ex.: apenas questionId) sobrescrevam o destino.
        const answerScopedRules = matchingRules.filter((rule) => {
            const when = rule?.when || {};
            return !!(when['answer.eq'] || when['answer.in'] || when?.answer?.eq || when?.answer?.in);
        });
        const effectiveRules = answerScopedRules.length ? answerScopedRules : matchingRules;

        if (effectiveRules.length) {
            effectiveRules.forEach((rule) => applyRuleActions(rule.do || [], context, maps));
        }
        if (context.nextQuestion === undefined) {
            context.nextQuestion = getDefaultNextQuestion(questionId, context, maps);
        }
        lastQuestionId = questionId;
    });

    if (context.nextQuestion === undefined) {
        context.nextQuestion = getDefaultNextQuestion(lastQuestionId, context, maps);
    }

    return context.nextQuestion;
};

const buildProgressGraph = (flow = null, questions = []) => {
    const maps = buildFlowMaps(flow || {});
    const adjacency = new Map();
    const addEdge = (from, to) => {
        if (!from) return;
        if (!adjacency.has(from)) adjacency.set(from, new Set());
        if (to) adjacency.get(from).add(to);
    };

    const getBlockFirstQuestion = (blockId) => {
        const blockQuestions = maps.blockMap.get(blockId) || [];
        return blockQuestions.length ? blockQuestions[0] : null;
    };

    // 1) Default edges by block order (fallback linear navigation).
    (flow?.blocks || []).forEach((block, blockIndex) => {
        const blockQuestions = Array.isArray(block.questions) ? block.questions : [];
        blockQuestions.forEach((questionId, questionIndex) => {
            if (questionIndex < blockQuestions.length - 1) {
                addEdge(questionId, blockQuestions[questionIndex + 1]);
                return;
            }
            for (let i = blockIndex + 1; i < (flow.blocks || []).length; i += 1) {
                const candidate = getBlockFirstQuestion(flow.blocks[i]?.id);
                if (candidate) {
                    addEdge(questionId, candidate);
                    break;
                }
            }
        });
    });

    // 2) Rule edges.
    (flow?.rules || []).forEach((rule) => {
        const from = rule?.when?.questionId;
        if (!from) return;
        (rule.do || []).forEach((action) => {
            if (action.gotoQuestion) addEdge(from, action.gotoQuestion);
            if (action.gotoBlock) addEdge(from, getBlockFirstQuestion(action.gotoBlock));
            if (action.enterSubflow) {
                const subflow = (flow.subflows || []).find((item) => item.id === action.enterSubflow);
                if (subflow?.start) addEdge(from, subflow.start);
            }
        });
    });

    // 3) Synthetic edges for explicit follow-up clips (q13r1/q13r2 etc).
    const questionIds = new Set((questions || []).map((q, idx) => q?.id || q?.video || `q${idx + 1}`));
    (questions || []).forEach((question, idx) => {
        const questionId = question?.id || question?.video || `q${idx + 1}`;
        if (!questionId) return;
        ['r1', 'r2', 'r3'].forEach((suffix) => {
            const candidate = `${questionId}${suffix}`;
            if (questionIds.has(candidate)) addEdge(questionId, candidate);
        });
    });

    return adjacency;
};

const collectReachable = (graph, startId, stopSet = new Set()) => {
    const visited = new Set();
    if (!startId) return visited;
    const queue = [startId];
    while (queue.length) {
        const node = queue.shift();
        if (!node || visited.has(node)) continue;
        if (stopSet.has(node)) continue;
        visited.add(node);
        const nextSet = graph.get(node);
        if (!nextSet) continue;
        nextSet.forEach((nextId) => {
            if (!visited.has(nextId)) queue.push(nextId);
        });
    }
    return visited;
};

const calculateInterviewProgress = ({ responses = [], flow = null, questions = [], isFinished = false, askedQuestionIds = [] } = {}) => {
    const sanitizedResponses = sanitizeResponsesForSave(Array.isArray(responses) ? responses : []);
    const orderedQuestionIds = buildQuestionOrder(flow, questions);
    const progressQuestionIds = buildProgressQuestionIdSet(questions);
    const baseTotal = Math.max(orderedQuestionIds.length || progressQuestionIds.size || 0, 1);
    const orderMap = new Map(orderedQuestionIds.map((id, idx) => [id, idx]));

    const answeredIds = new Set();
    sanitizedResponses.forEach((response) => {
        const questionId = getQuestionIdFromResponse(response, questions);
        if (questionId && progressQuestionIds.has(questionId)) answeredIds.add(questionId);
    });

    const askedIds = new Set(
        (Array.isArray(askedQuestionIds) ? askedQuestionIds : [])
            .filter((id) => !!id && progressQuestionIds.has(id))
    );
    answeredIds.forEach((id) => askedIds.add(id));

    if (flow && progressQuestionIds.size) {
        const graph = buildProgressGraph(flow, questions);
        const nextQuestionId = determineNextQuestionId(sanitizedResponses, flow, questions);
        const remainingSet = new Set();
        if (!isFinished && nextQuestionId && progressQuestionIds.has(nextQuestionId)) {
            const reachable = collectReachable(graph, nextQuestionId);
            remainingSet.add(nextQuestionId);
            reachable.forEach((id) => {
                if (progressQuestionIds.has(id)) remainingSet.add(id);
            });
        }
        answeredIds.forEach((id) => remainingSet.delete(id));

        // Se há pergunta já exibida e ainda não respondida, ela precisa contar no denominador.
        const pendingAsked = new Set();
        askedIds.forEach((id) => {
            if (!answeredIds.has(id)) pendingAsked.add(id);
        });
        pendingAsked.forEach((id) => remainingSet.add(id));

        const effectiveTotal = Math.max(1, answeredIds.size + remainingSet.size);
        const answered = Math.min(answeredIds.size, effectiveTotal);
        const displayAnswered = isFinished ? effectiveTotal : answered;
        let percent = isFinished ? 100 : Math.round((answered / effectiveTotal) * 100);
        if (!isFinished && answered > 0) {
            percent = Math.max(1, percent);
        }
        percent = Math.max(0, Math.min(100, percent));
        return {
            percent,
            answered: displayAnswered,
            total: effectiveTotal,
            baseTotal,
            skippedCount: Math.max(0, baseTotal - effectiveTotal),
        };
    }

    const skippedIds = new Set();
    if (flow && sanitizedResponses.length && orderMap.size) {
        const graph = buildProgressGraph(flow, questions);
        sanitizedResponses.forEach((response, idx) => {
            const currentQuestionId = getQuestionIdFromResponse(response, questions);
            if (!currentQuestionId) return;

            const nextQuestionId = determineNextQuestionId(
                sanitizedResponses.slice(0, idx + 1),
                flow,
                questions
            );
            const outgoing = Array.from(graph.get(currentQuestionId) || []);
            if (!outgoing.length) return;
            const chosenNext = nextQuestionId || null;
            const chosenFuture = chosenNext ? collectReachable(graph, chosenNext) : new Set();
            if (chosenNext) chosenFuture.add(chosenNext);

            outgoing
                .filter((candidate) => candidate && candidate !== chosenNext)
                .forEach((candidate) => {
                    const candidateOnlyNodes = collectReachable(graph, candidate, chosenFuture);
                    candidateOnlyNodes.forEach((nodeId) => {
                        if (progressQuestionIds.has(nodeId)) skippedIds.add(nodeId);
                    });
                });
        });
    }

    answeredIds.forEach((id) => skippedIds.delete(id));
    const effectiveTotal = Math.max(1, baseTotal - skippedIds.size);
    const answered = Math.min(answeredIds.size, effectiveTotal);
    const displayAnswered = isFinished ? effectiveTotal : answered;
    let percent = isFinished ? 100 : Math.round((answered / effectiveTotal) * 100);
    if (!isFinished && answered > 0) {
        percent = Math.max(1, percent);
    }
    percent = Math.max(0, Math.min(100, percent));

    return {
        percent,
        answered: displayAnswered,
        total: effectiveTotal,
        baseTotal,
        skippedCount: skippedIds.size,
    };
};

const sanitizeResponsesForSave = (responses = []) => {
    if (!Array.isArray(responses) || !responses.length) return [];
    const merged = [];
    const indexMap = new Map();

    const mergeResponseEntry = (existing, incoming) => {
        const mergedEntry = { ...existing, ...incoming };
        if (!incoming.followKey && existing.followKey) {
            mergedEntry.followKey = existing.followKey;
        }
        if (!incoming.followUpResponse && existing.followUpResponse) {
            mergedEntry.followUpResponse = existing.followUpResponse;
        }
        if (!incoming.userResponse && existing.userResponse) {
            mergedEntry.userResponse = existing.userResponse;
        }
        return mergedEntry;
    };

    responses.forEach((item) => {
        if (!item || typeof item !== 'object') return;
        const hasIndex = Number.isFinite(item.qIndex);
        if (!hasIndex) {
            merged.push(item);
            return;
        }
        const key = item.qIndex;
        if (indexMap.has(key)) {
            const idx = indexMap.get(key);
            merged[idx] = mergeResponseEntry(merged[idx], item);
        } else {
            indexMap.set(key, merged.length);
            merged.push(item);
        }
    });

    return merged;
};

export {
    getLocalFlowDefinition,
    determineNextQuestionId,
    calculateInterviewProgress,
    sanitizeResponsesForSave,
    toNumberArray,
    normalizeYesNo,
    isYesNoQuestion,
    getInvalidAnswerConfig,
    normalizeAnswerForQuestion,
    shouldEndAfterVideo,
    shouldEndAfterVideoForClip,
    getFinalQuestion,
    getFinalClipKey,
    getIntroClipKey
};





